import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { Badge, Btn, eur } from "./common";
import { toast } from "sonner";
import { Plus, RefreshCw, Pencil, Trash2, X, Upload } from "lucide-react";

const PERIODO_LABEL = { anual: "Anual", "3anos": "3 anos", "5anos": "5 anos" };
const PERIODO_ORDER = ["anual", "3anos", "5anos"];
const round2 = (v) => Math.round(v * 100) / 100;

export function CatalogPlansTab({ platformId, platformSlug }) {
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const isCE = (platformSlug || "").toLowerCase().includes("cognietica");
  const isPV = (platformSlug || "").toLowerCase().includes("privaxia");

  const load = () => {
    if (!platformId) return;
    api.get(`/plans?plataforma_id=${platformId}`).then((r) => setPlans(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [platformId]);

  const remove = async (plan) => {
    if (!confirm(`Eliminar plano "${plan.nome}"?`)) return;
    try { await api.delete(`/plans/${plan.id}`); toast.success("Plano eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const EMPTY = {
    plataforma_id: platformId, nome: "", sku: "", upstream_plan_key: "",
    preco_mensal_1ano: 0, preco_mensal_3anos: 0, preco_mensal_5anos: 0,
    desconto_3anos: 10, desconto_5anos: 20,
    periodos_disponiveis: ["anual", "3anos", "5anos"],
    moeda: "EUR", taxa_iva_regiao: 23, setup_fee: 0, trial_dias: 0,
    limites: "", ativo: true,
    max_utilizadores: 0,
    addon_triagem_mensal: 0, addon_dpo_mensal: 0,
    max_denuncias: isCE ? 50 : -1,
    valor_extra_denuncia: isCE ? 5 : 0,
    max_titulares: isPV ? 1000 : -1,
  };

  const [pushing, setPushing] = useState(false);
  const [connectorOk, setConnectorOk] = useState(null); // null=unknown, true/false

  useEffect(() => {
    // Verificar se o conector upstream suporta gestao de planos (silencioso)
    const slug = platformSlug?.toLowerCase();
    if (slug === "cognietica" || slug === "privaxia") {
      api.get(`/${slug}/plans`)
        .then(() => setConnectorOk(true))
        .catch(() => setConnectorOk(false));
    }
  }, [platformSlug]);

  const pushToUpstream = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/plans/push-to-upstream/${platformId}`);
      if (data.pushed > 0 && !data.errors?.length) toast.success(`${data.pushed} plano(s) sincronizado(s) para a plataforma`);
      else if (data.pushed > 0 && data.errors?.length > 0) toast.success(`${data.pushed} sincronizado(s), ${data.errors.length} falharam`);
      else if (data.errors?.length > 0) {
        const first = data.errors[0] || {};
        const reason = first.error || "";
        const sku = first.sku ? ` [${first.sku}]` : "";
        toast.error(`Falha ao sincronizar ${data.errors.length} plano(s)${sku}. ${reason}`, { duration: 15000 });
      }
      else toast.info("Nenhum plano para sincronizar. Verifique se os planos tem SKU definido.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPushing(false); }
  };

  const healthStats = (() => {
    const total = plans.length;
    if (total === 0) return null;
    const now = Date.now();
    const _24h = 24 * 60 * 60 * 1000;
    let synced24h = 0, stale = 0, never = 0;
    for (const p of plans) {
      const ts = p.upstream_synced_at ? new Date(p.upstream_synced_at).getTime() : 0;
      if (!ts) never += 1;
      else if (now - ts <= _24h) synced24h += 1;
      else stale += 1;
    }
    const pct = Math.round((synced24h / total) * 100);
    return { total, synced24h, stale, never, pct };
  })();

  const healthTone = (() => {
    if (!healthStats) return "muted";
    if (healthStats.pct >= 90) return "ok";
    if (healthStats.pct >= 60) return "warn";
    return "bad";
  })();

  return (
    <div>
      {healthStats && (connectorOk === true) && (
        <div className="mb-4 flex flex-wrap gap-3 items-stretch" data-testid="catalog-health-card">
          <div className={`rounded-lg border px-4 py-3 flex-1 min-w-[220px] ${
            healthTone === "ok" ? "border-emerald-200 bg-emerald-50" :
            healthTone === "warn" ? "border-amber-200 bg-amber-50" :
            "border-red-200 bg-red-50"
          }`}>
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-navy/70">Saude Sync 24h</span>
              <span data-testid="catalog-health-pct" className={`text-2xl font-bold ${
                healthTone === "ok" ? "text-emerald-700" :
                healthTone === "warn" ? "text-amber-700" :
                "text-red-600"
              }`}>{healthStats.pct}%</span>
            </div>
            <div className="text-[11px] text-navy/70 mt-1">
              {healthStats.synced24h} de {healthStats.total} planos sincronizados nas ultimas 24h
            </div>
          </div>
          <div className="rounded-lg border border-tint px-4 py-3 min-w-[140px] bg-white">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Recentes</div>
            <div className="text-xl font-bold text-navy mt-0.5" data-testid="catalog-health-fresh">{healthStats.synced24h}</div>
            <div className="text-[10px] text-muted2">Push OK &lt; 24h</div>
          </div>
          <div className="rounded-lg border border-tint px-4 py-3 min-w-[140px] bg-white">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Antigos</div>
            <div className="text-xl font-bold text-navy mt-0.5" data-testid="catalog-health-stale">{healthStats.stale}</div>
            <div className="text-[10px] text-muted2">Push OK &gt; 24h</div>
          </div>
          <div className="rounded-lg border border-tint px-4 py-3 min-w-[140px] bg-white">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Nunca</div>
            <div className="text-xl font-bold text-navy mt-0.5" data-testid="catalog-health-never">{healthStats.never}</div>
            <div className="text-[10px] text-muted2">Sem push registado</div>
          </div>
        </div>
      )}

      <div className="flex justify-end mb-4 gap-2">
        {connectorOk === true && (
          <Btn variant="ghost" onClick={pushToUpstream} disabled={pushing} testid="catalog-tab-push-upstream">
            <Upload size={16} /> {pushing ? "A sincronizar..." : "Sincronizar para plataforma"}
          </Btn>
        )}
        {connectorOk === false && (
          <span className="text-[10px] text-muted2 bg-amber-50 px-2 py-1.5 rounded self-center" data-testid="catalog-tab-upstream-offline">
            Conector upstream indisponivel - gestao local activa
          </span>
        )}
        <Btn variant="ghost" onClick={load}><RefreshCw size={16} /> Atualizar</Btn>
        <Btn onClick={() => setEdit({ ...EMPTY, _new: true })} testid="catalog-tab-new-plan">
          <Plus size={16} /> Novo plano
        </Btn>
      </div>

      {plans.length === 0 && <p className="text-muted2 text-sm">Sem planos configurados no catalogo para esta plataforma.</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <div key={plan.id} className="border border-tint rounded-lg p-4 hover:border-blue/30 transition-colors bg-white" data-testid={`catalog-tab-plan-${plan.sku}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-navy text-sm">{plan.nome}</span>
              <Badge value={plan.ativo ? "ativo" : "inativo"} />
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <code className="text-[10px] bg-tint px-2 py-0.5 rounded text-muted2">{plan.sku}</code>
              {connectorOk === true && plan.upstream_synced_at && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium" title={`Sincronizado: ${plan.upstream_synced_at}`}>Upstream OK</span>
              )}
              {plan.max_utilizadores > 0 && (
                <span className="text-[10px] bg-navy/10 text-navy px-2 py-0.5 rounded-full font-semibold">
                  {plan.max_utilizadores} utilizador{plan.max_utilizadores !== 1 ? "es" : ""}
                </span>
              )}
            </div>
            {/* Precos mensais */}
            <div className="space-y-1 mt-3">
              {(plan.periodos_disponiveis || []).map((per) => {
                const mensalMap = { anual: plan.preco_mensal_1ano, "3anos": plan.preco_mensal_3anos, "5anos": plan.preco_mensal_5anos };
                const totalMap = { anual: plan.preco_anual, "3anos": plan.preco_3anos, "5anos": plan.preco_5anos };
                const mensal = mensalMap[per] || 0;
                const total = totalMap[per] || 0;
                if (!mensal && !total) return null;
                return (
                  <div key={per} className="flex items-center justify-between text-xs">
                    <span className="text-muted2">{PERIODO_LABEL[per] || per}</span>
                    <span><b className="text-navy">{eur(mensal)}/mes</b><span className="text-muted2 ml-1">({eur(total)} total)</span></span>
                  </div>
                );
              })}
            </div>
            {/* Limites plataforma */}
            {isCE && (
              <div className="mt-2 pt-2 border-t border-dashed border-tint space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted2">Denuncias incluidas/mes</span>
                  <b className="text-navy">{plan.max_denuncias === -1 ? "Ilimitado" : plan.max_denuncias}</b>
                </div>
                {plan.valor_extra_denuncia > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted2">Valor extra/denuncia</span>
                    <b className="text-amber-700">{eur(plan.valor_extra_denuncia)}/un</b>
                  </div>
                )}
              </div>
            )}
            {isPV && plan.max_titulares !== -1 && (
              <div className="mt-2 pt-2 border-t border-dashed border-tint">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted2">Max titulares</span>
                  <b className="text-navy">{plan.max_titulares === -1 ? "Ilimitado" : plan.max_titulares}</b>
                </div>
              </div>
            )}
            {/* Add-ons */}
            {(plan.addon_triagem_mensal > 0 || plan.addon_dpo_mensal > 0) && (
              <div className="mt-2 pt-2 border-t border-dashed border-tint space-y-1">
                <span className="text-[10px] uppercase font-semibold text-muted2 tracking-wide">Add-ons servico interno</span>
                {plan.addon_triagem_mensal > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted2">Gestao Triagem</span>
                    <b className="text-emerald-700">{eur(plan.addon_triagem_mensal)}/mes</b>
                  </div>
                )}
                {plan.addon_dpo_mensal > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted2">DPO as a Service</span>
                    <b className="text-emerald-700">{eur(plan.addon_dpo_mensal)}/mes</b>
                  </div>
                )}
              </div>
            )}
            {/* Acoes */}
            <div className="flex gap-1 mt-3 pt-2 border-t border-tint">
              <button onClick={() => setEdit({ ...plan })} data-testid={`catalog-tab-edit-${plan.sku}`}
                className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white inline-flex items-center gap-1">
                <Pencil size={11} /> Editar
              </button>
              <button onClick={() => remove(plan)} data-testid={`catalog-tab-del-${plan.sku}`}
                className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <CatalogPlanModal
          plan={edit}
          platformId={platformId}
          isCE={isCE}
          isPV={isPV}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }}
        />
      )}
    </div>
  );
}

function CatalogPlanModal({ plan, platformId, isCE, isPV, onClose, onSaved }) {
  const isNew = plan._new;
  const [form, setForm] = useState({
    plataforma_id: platformId,
    nome: plan.nome || "", sku: plan.sku || "",
    upstream_plan_key: plan.upstream_plan_key || plan.sku || "",
    preco_mensal_1ano: plan.preco_mensal_1ano || 0,
    preco_mensal_3anos: plan.preco_mensal_3anos || 0,
    preco_mensal_5anos: plan.preco_mensal_5anos || 0,
    desconto_3anos: plan.desconto_3anos ?? 10,
    desconto_5anos: plan.desconto_5anos ?? 20,
    periodos_disponiveis: plan.periodos_disponiveis || ["anual", "3anos", "5anos"],
    moeda: plan.moeda || "EUR",
    taxa_iva_regiao: plan.taxa_iva_regiao ?? 23,
    setup_fee: plan.setup_fee || 0,
    trial_dias: plan.trial_dias || 0,
    limites: plan.limites || "",
    ativo: plan.ativo !== false,
    max_utilizadores: plan.max_utilizadores || 0,
    addon_triagem_mensal: plan.addon_triagem_mensal || 0,
    addon_dpo_mensal: plan.addon_dpo_mensal || 0,
    max_denuncias: plan.max_denuncias ?? -1,
    valor_extra_denuncia: plan.valor_extra_denuncia || 0,
    max_titulares: plan.max_titulares ?? -1,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const base = Number(form.preco_mensal_1ano) || 0;
  const calc3 = base > 0 ? round2(base * (1 - (Number(form.desconto_3anos) || 0) / 100)) : 0;
  const calc5 = base > 0 ? round2(base * (1 - (Number(form.desconto_5anos) || 0) / 100)) : 0;

  const updBase = (v) => {
    const b = Number(v) || 0;
    setForm((f) => ({
      ...f, preco_mensal_1ano: v,
      preco_mensal_3anos: round2(b * (1 - (Number(f.desconto_3anos) || 0) / 100)),
      preco_mensal_5anos: round2(b * (1 - (Number(f.desconto_5anos) || 0) / 100)),
    }));
  };
  const updDesc = (key, v) => {
    setForm((f) => {
      const b = Number(f.preco_mensal_1ano) || 0;
      const mk = key === "desconto_3anos" ? "preco_mensal_3anos" : "preco_mensal_5anos";
      return { ...f, [key]: v, [mk]: round2(b * (1 - (Number(v) || 0) / 100)) };
    });
  };

  const save = async () => {
    if (!form.nome.trim() || !form.sku.trim()) return toast.error("Preencha nome e SKU.");
    if (base <= 0) return toast.error("Defina o preco base mensal (1 ano).");
    setBusy(true);
    try {
      const body = {
        ...form,
        preco_mensal_1ano: base, preco_mensal_3anos: calc3, preco_mensal_5anos: calc5,
        desconto_3anos: Number(form.desconto_3anos) || 0, desconto_5anos: Number(form.desconto_5anos) || 0,
        preco: base * 12, preco_anual: base * 12, preco_3anos: calc3 * 36, preco_5anos: calc5 * 60,
        taxa_iva_regiao: Number(form.taxa_iva_regiao || 23), setup_fee: Number(form.setup_fee || 0),
        trial_dias: Number(form.trial_dias || 0), max_utilizadores: Number(form.max_utilizadores || 0),
        addon_triagem_mensal: Number(form.addon_triagem_mensal || 0), addon_dpo_mensal: Number(form.addon_dpo_mensal || 0),
        max_denuncias: Number(form.max_denuncias ?? -1), valor_extra_denuncia: Number(form.valor_extra_denuncia || 0),
        max_titulares: Number(form.max_titulares ?? -1),
      };
      if (isNew) {
        await api.post("/plans", body);
        toast.success("Plano criado");
      } else {
        await api.put(`/plans/${plan.id}`, body);
        toast.success("Plano guardado");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="catalog-tab-plan-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-navy text-lg font-bold tracking-tight">{isNew ? "Novo plano" : `Editar ${form.nome}`}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Nome</label>
            <input value={form.nome} onChange={(e) => upd("nome", e.target.value)} data-testid="ctab-plan-nome"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">SKU</label>
            <input value={form.sku} onChange={(e) => { upd("sku", e.target.value); if (!form.upstream_plan_key || form.upstream_plan_key === form.sku) upd("upstream_plan_key", e.target.value); }} data-testid="ctab-plan-sku"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold text-navy mb-1">Chave upstream (usada para sincronizar com a plataforma)</label>
          <input value={form.upstream_plan_key} onChange={(e) => upd("upstream_plan_key", e.target.value)} data-testid="ctab-plan-upstream-key"
            placeholder={form.sku || "ex: starter"}
            className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
        </div>

        {/* Periodos disponiveis */}
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-semibold text-navy">Periodos:</span>
          {PERIODO_ORDER.map((per) => (
            <label key={per} className="inline-flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={(form.periodos_disponiveis || []).includes(per)}
                onChange={(e) => {
                  const cur = form.periodos_disponiveis || [];
                  upd("periodos_disponiveis", e.target.checked ? [...cur, per] : cur.filter((x) => x !== per));
                }} />
              {PERIODO_LABEL[per]}
            </label>
          ))}
        </div>

        {/* Precos com descontos */}
        <div className="border border-tint rounded-lg p-3 mb-3 bg-slate-50/60">
          <div className="text-xs font-semibold text-navy mb-3">Precos mensais por compromisso</div>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Preco base mensal (1 ano)</label>
              <input type="number" value={form.preco_mensal_1ano} onChange={(e) => updBase(e.target.value)} data-testid="ctab-plan-base"
                className="w-full px-2 py-1.5 border border-tint rounded text-sm" step="0.01" />
              {base > 0 && <div className="text-[10px] text-muted2 mt-0.5">Total anual: {eur(base * 12)}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Desconto 3 anos (%)</label>
                <input type="number" value={form.desconto_3anos} onChange={(e) => updDesc("desconto_3anos", e.target.value)} data-testid="ctab-plan-desc3"
                  className="w-full px-2 py-1.5 border border-tint rounded text-sm" step="0.5" min="0" max="100" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Mensal (3 anos)</label>
                <div className="px-2 py-1.5 border border-tint rounded text-sm bg-white text-navy font-semibold">{eur(calc3)}/mes</div>
                <div className="text-[10px] text-muted2 mt-0.5">Total: {eur(calc3 * 36)}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Desconto 5 anos (%)</label>
                <input type="number" value={form.desconto_5anos} onChange={(e) => updDesc("desconto_5anos", e.target.value)} data-testid="ctab-plan-desc5"
                  className="w-full px-2 py-1.5 border border-tint rounded text-sm" step="0.5" min="0" max="100" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-navy uppercase tracking-wide mb-1">Mensal (5 anos)</label>
                <div className="px-2 py-1.5 border border-tint rounded text-sm bg-white text-navy font-semibold">{eur(calc5)}/mes</div>
                <div className="text-[10px] text-muted2 mt-0.5">Total: {eur(calc5 * 60)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Colaboradores + Add-ons */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Max utilizadores</label>
            <input type="number" value={form.max_utilizadores} onChange={(e) => upd("max_utilizadores", e.target.value)} data-testid="ctab-plan-max-users"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" min="0" />
            <div className="text-[10px] text-muted2 mt-0.5">User Cogniseg (triagem/DPO) nao conta</div>
          </div>
          {isCE && (
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Add-on Triagem/mes</label>
              <input type="number" value={form.addon_triagem_mensal} onChange={(e) => upd("addon_triagem_mensal", e.target.value)}
                className="w-full px-3 py-2 border border-tint rounded-lg text-sm" step="0.01" />
            </div>
          )}
          {isPV && (
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Add-on DPO/mes</label>
              <input type="number" value={form.addon_dpo_mensal} onChange={(e) => upd("addon_dpo_mensal", e.target.value)}
                className="w-full px-3 py-2 border border-tint rounded-lg text-sm" step="0.01" />
            </div>
          )}
        </div>

        {/* Campos plataforma-especificos */}
        {isCE && (
          <div className="border border-amber-200 rounded-lg p-3 mb-3 bg-amber-50/40">
            <div className="text-xs font-semibold text-amber-800 mb-2">Limites CogniEtica - Denuncias</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Max denuncias/mes (-1=ilim.)</label>
                <input type="number" value={form.max_denuncias} onChange={(e) => upd("max_denuncias", e.target.value)} data-testid="ctab-plan-max-den"
                  className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Valor extra/denuncia</label>
                <input type="number" value={form.valor_extra_denuncia} onChange={(e) => upd("valor_extra_denuncia", e.target.value)} data-testid="ctab-plan-extra-den"
                  className="w-full px-3 py-2 border border-tint rounded-lg text-sm" step="0.01" />
              </div>
            </div>
          </div>
        )}
        {isPV && (
          <div className="border border-blue/20 rounded-lg p-3 mb-3 bg-blue/5">
            <div className="text-xs font-semibold text-blue mb-2">Limites Privaxia - Titulares</div>
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Max titulares (-1=ilim.)</label>
              <input type="number" value={form.max_titulares} onChange={(e) => upd("max_titulares", e.target.value)} data-testid="ctab-plan-max-tit"
                className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
            </div>
          </div>
        )}

        {/* Outros */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">IVA (%)</label>
            <input type="number" value={form.taxa_iva_regiao} onChange={(e) => upd("taxa_iva_regiao", e.target.value)}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Setup fee</label>
            <input type="number" value={form.setup_fee} onChange={(e) => upd("setup_fee", e.target.value)}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" step="0.01" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Trial (dias)</label>
            <input type="number" value={form.trial_dias} onChange={(e) => upd("trial_dias", e.target.value)}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" min="0" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" checked={form.ativo} onChange={(e) => upd("ativo", e.target.checked)} /> Plano ativo
        </label>

        <div className="flex gap-2 justify-end pt-2 border-t border-tint">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={busy} testid="ctab-plan-save">{busy ? "..." : "Guardar"}</Btn>
        </div>
      </div>
    </div>
  );
}
